import { Component, h, Prop, State, Event, EventEmitter, Listen, Element, Build } from '@stencil/core';
import { parseJsonProp } from '../../../utils/helpers/hydration';

interface Option {
  value: string;
  label: string;
}

/**
 * `<me-select>` — Custom Select Component
 *
 * Features:
 * - Custom dropdown select with keyboard and mouse support
 * - Supports placeholder, label, required, disabled, and custom error messages
 * - Dynamic dropdown positioning (up/down) based on available space
 * - Max dropdown height with scroll, adaptive to viewport
 * - Emits valueChange and clearFieldError events
 * - Accessibility: full keyboard navigation, ARIA roles, label association
 * - Animated chevron arrow, custom option highlighting, and error display
 * - Automatically selects first option if no placeholder and no value
 *
 * @example
 *   <me-select label="Country" name="country" options='[{"value": "us", "label": "United States"}, {"value": "uk", "label": "United Kingdom"}]'></me-select>
 */

@Component({
  tag: 'me-select',
  styleUrl: 'index.scss',
  shadow: false,
})
export class Select {
  @Element() hostElement: HTMLElement;

  /**
   * Placeholder text shown when no option is selected
   * @default ''
   */
  @Prop() placeholder: string = '';

  /**
   * Currently selected value
   */
  @Prop({ mutable: true }) value: string = '';

  /**
   * Disables the select if true
   * @default false
   */
  @Prop() disabled: boolean = false;

  /**
   * Name attribute for the select element (for forms)
   */
  @Prop() name: string = '';

  /**
   * Marks the select as required
   * @default false
   */
  @Prop() required: boolean = false;

  /**
   * Options to display in the dropdown.
   *
   * Accepts either a real array (set via JS property, e.g. Storybook `.options=` or a
   * parent component's `options={array}`) or a JSON string (set via the `options` HTML
   * attribute, e.g. when authored in markup or rendered by Herald). The `| string` part
   * is required so Stencil observes the `options` attribute; it's parsed in
   * `componentWillLoad`. Internal reads should use the `optionList` getter.
   * @minItems 1
   */
  @Prop({ mutable: true }) options: Option[] | string = [];

  /**
   * Type-safe view of `options` as an array, regardless of how it was supplied.
   */
  private get optionList(): Option[] {
    return Array.isArray(this.options) ? this.options : [];
  }

  /**
   * Event emitted when the selected value changes
   */
  @Event() valueChange: EventEmitter<string>;

  /**
   * Text label displayed above the select
   * @default ''
   */
  @Prop() label?: string;

  /**
   * Error state for select (set by form handler)
   */
  @Prop() error?: string;

  /**
   * Error message for select (set by form handler)
   */
  @Prop() errorMessage?: string;

  /**
   * Custom message for required error
   */
  @Prop() requiredMessage?: string;

  /**
   * Custom message for invalid error
   */
  @Prop() invalidMessage?: string;

  /**
   * Error type: 'required' | 'invalid' (set by form handler)
   */
  @Prop() errorType?: 'required' | 'invalid';

  @State() isOpen: boolean = false;
  @State() selectedLabel: string = '';
  @State() focusedOptionIndex: number = -1;
  @State() dropdownPosition: 'top' | 'bottom' = 'bottom';
  @State() dropdownCoords: { top: number; left: number; width: number } | null = null;

  private triggerRef?: HTMLButtonElement;
  private optionRefs: HTMLButtonElement[] = [];

  private scrollableParents: Element[] = [];
  private positionUpdateScheduled: boolean = false;
  private resizeObserver: ResizeObserver;

  @Event() clearFieldError: EventEmitter<string>;

  private schedulePositionUpdate = () => {
    if (!this.positionUpdateScheduled) {
      this.positionUpdateScheduled = true;
      requestAnimationFrame(() => {
        this.calculateDropdownPosition();
        this.positionUpdateScheduled = false;
      });
    }
  };

  @Listen('scroll', { target: 'window' })
  handleWindowScroll() {
    if (this.isOpen) {
      this.schedulePositionUpdate();
    }
  }

  private handleParentScroll = () => {
    if (this.isOpen) {
      this.schedulePositionUpdate();
    }
  };

  private calculateDropdownPosition = () => {
    if (!Build.isBrowser || !this.isOpen) return;

    const parentRect = this.hostElement.getBoundingClientRect();
    const triggerRect = this.triggerRef.getBoundingClientRect();
    const dropdownEl = this.hostElement.querySelector('.select__dropdown') as HTMLElement;
    if (!dropdownEl) return;

    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - triggerRect.bottom;
    const spaceAbove = triggerRect.top;

    const MAX_DROPDOWN_HEIGHT = 288;
    const PADDING = 4; // offset from the edge of the screen
    const MIN_DROPDOWN_HEIGHT = 80;

    const availableBelow = Math.max(spaceBelow - PADDING, MIN_DROPDOWN_HEIGHT);
    const availableAbove = Math.max(spaceAbove - PADDING, MIN_DROPDOWN_HEIGHT);

    const fitsBelow = dropdownEl.scrollHeight <= availableBelow;
    const fitsAbove = dropdownEl.scrollHeight <= availableAbove;
    const shouldShowBelow = fitsBelow || (!fitsAbove && availableBelow >= availableAbove);

    const maxHeight = Math.min(shouldShowBelow ? availableBelow : availableAbove, MAX_DROPDOWN_HEIGHT);
    dropdownEl.style.maxHeight = `${maxHeight}px`;

    // New positioning: strictly below or above select__trigger
    if (shouldShowBelow) {
      dropdownEl.style.top = `${this.triggerRef.offsetTop + this.triggerRef.offsetHeight + 14}px`;
      dropdownEl.style.bottom = '';
      dropdownEl.style.maxHeight = `${maxHeight}px`;
      this.dropdownCoords = {
        left: triggerRect.left - parentRect.left,
        width: triggerRect.width,
        top: parseFloat(dropdownEl.style.top),
      };
    } else {
      dropdownEl.style.maxHeight = `${maxHeight}px`;
      requestAnimationFrame(() => {
        dropdownEl.style.top = `${this.triggerRef.offsetTop - dropdownEl.offsetHeight - 5}px`;
        dropdownEl.style.bottom = '';
        this.dropdownCoords = {
          left: triggerRect.left - parentRect.left,
          width: triggerRect.width,
          top: parseFloat(dropdownEl.style.top),
        };
      });
      return;
    }

    this.hostElement.classList.toggle('select--top', !shouldShowBelow);
    this.hostElement.classList.toggle('select--bottom', shouldShowBelow);
  };

  private cleanupPositionTracking() {
    // Remove scroll listeners
    this.scrollableParents.forEach(parent => {
      parent.removeEventListener('scroll', this.handleParentScroll);
    });

    // Disconnect ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.scrollableParents = [];
  }

  @Listen('keydown')
  handleKeyDown(event: KeyboardEvent) {
    if (this.disabled) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        if (!this.isOpen) {
          this.openDropdown();
        } else if (this.focusedOptionIndex >= 0) {
          this.selectOption(this.optionList[this.focusedOptionIndex]);
        }
        event.preventDefault();
        break;

      case 'Escape':
        if (this.isOpen) {
          this.closeDropdown();
          this.triggerRef?.focus();
          // Prevent event propagation to stop modal from closing
          event.stopPropagation();
          event.preventDefault();
        }
        break;

      case 'ArrowDown':
        if (!this.isOpen) {
          this.openDropdown();
        } else {
          this.focusNextOption();
        }
        event.preventDefault();
        break;

      case 'ArrowUp':
        if (!this.isOpen) {
          this.openDropdown();
        } else {
          this.focusPreviousOption();
        }
        event.preventDefault();
        break;

      case 'Tab':
        if (this.isOpen) {
          // If there's a focused option, confirm the selection
          if (this.focusedOptionIndex >= 0) {
            this.selectOption(this.optionList[this.focusedOptionIndex]);
          }
          this.closeDropdown();
        }
        // Don't prevent default Tab behavior
        break;

      case 'Home':
        if (this.isOpen && this.optionList.length > 0) {
          this.focusedOptionIndex = 0;
          this.scrollToFocusedOption();
        }
        event.preventDefault();
        break;

      case 'End':
        if (this.isOpen && this.optionList.length > 0) {
          this.focusedOptionIndex = this.optionList.length - 1;
          this.scrollToFocusedOption();
        }
        event.preventDefault();
        break;
    }
  }

  @Listen('click', { target: 'window' })
  handleWindowClick(event: MouseEvent) {
    const target = event.target as HTMLElement;

    // If click was outside the select
    if (!this.isElementInsideSelect(target)) {
      // Close dropdown if it's open
      if (this.isOpen) {
        this.closeDropdown();
      }
      // Don't capture focus if click was on an interactive element
      if (target.matches('input, textarea, button, select, [contenteditable]')) {
        return;
      }
    }
  }

  @Listen('resize', { target: 'window' })
  handleResize() {
    if (this.isOpen) {
      this.calculateDropdownPosition();
    }
  }

  private scrollToFocusedOption = () => {
    requestAnimationFrame(() => {
      if (this.focusedOptionIndex >= 0) {
        this.optionRefs[this.focusedOptionIndex]?.scrollIntoView({ block: 'nearest' });
      }
    });
  };

  private focusNextOption = () => {
    if (this.focusedOptionIndex < this.optionList.length - 1) {
      this.focusedOptionIndex++;
      this.scrollToFocusedOption();
    }
  };

  private focusPreviousOption = () => {
    if (this.focusedOptionIndex > 0) {
      this.focusedOptionIndex--;
      this.scrollToFocusedOption();
    }
  };

  private toggleDropdown = (event: MouseEvent) => {
    event.stopPropagation(); // Prevent click event bubbling

    if (this.isOpen) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  };

  private selectOption = (option: Option) => {
    if (option.value !== this.value) {
      this.value = option.value;
      this.valueChange.emit(option.value);
      // Hide error when selecting an option
      this.clearFieldError.emit(this.getResolvedName());
    }
    this.closeDropdown();
  };

  private handleClickOutside = (event: MouseEvent) => {
    const target = event.target as HTMLElement;
    if (!target.closest('.select')) {
      this.closeDropdown();
    }
  };

  private handleSelectChange = (event: Event) => {
    const select = event.target as HTMLSelectElement;
    this.value = select.value;
    this.valueChange.emit(this.value);
    this.clearFieldError.emit(this.getResolvedName());
    // Clear error attributes on any change
    (this as any).hostElement?.removeAttribute('error');
    (this as any).hostElement?.removeAttribute('error-message');
    (this as any).hostElement?.removeAttribute('error-type');
  };

  componentWillLoad() {
    // Normalize options: accept a JSON string (HTML attribute / Herald) or a real array.
    this.options = parseJsonProp<Option[]>(this.options, []) ?? [];
    // If there is no placeholder and value is not set, select the first option by default
    if (!this.placeholder && !this.value && this.optionList.length > 0) {
      this.value = this.optionList[0].value;
    }
    // Set initial label if value exists
    const selectedOption = this.optionList.find(opt => opt.value === this.value);
    if (selectedOption) {
      this.selectedLabel = selectedOption.label;
    }
    // Add listener for closing on click outside (browser only)
    if (Build.isBrowser) {
      document.addEventListener('click', this.handleClickOutside);
    }
  }

  disconnectedCallback() {
    if (!Build.isBrowser) return;
    this.cleanupPositionTracking();
    document.removeEventListener('click', this.handleClickOutside);
  }

  private openDropdown = () => {
    if (!this.disabled) {
      this.isOpen = true;
      this.triggerRef?.focus();
      const selectedIdx = this.optionList.findIndex(opt => opt.value === this.value);
      this.focusedOptionIndex = selectedIdx >= 0 ? selectedIdx : 0;
      this.clearFieldError.emit(this.getResolvedName());
      this.updateDropdownCoords();
      setTimeout(() => {
        this.calculateDropdownPosition();
        this.scrollToFocusedOption();
      }, 0);
    }
  };

  private closeDropdown = () => {
    this.cleanupPositionTracking();
    this.isOpen = false;
    this.focusedOptionIndex = -1;
  };

  /**
   * Checks if the target element is inside the select component
   */
  private isElementInsideSelect = (element: Element | null): boolean => {
    if (!element) return false;
    return this.hostElement.contains(element);
  };

  @Listen('focusin', { target: 'window' })
  handleFocusIn(event: FocusEvent) {
    const target = event.target as Element;
    const relatedTarget = event.relatedTarget as Element;

    // If focus moves to an element outside select, close dropdown
    if (!this.isElementInsideSelect(target) && this.isElementInsideSelect(relatedTarget)) {
      this.closeDropdown();
    }
  }

  @Listen('focusout', { target: 'window' })
  handleFocusOut(event: FocusEvent) {
    const target = event.target as Element;
    const relatedTarget = event.relatedTarget as Element;

    // If focus moves from an element inside select to outside, close dropdown
    if (this.isElementInsideSelect(target) && !this.isElementInsideSelect(relatedTarget)) {
      this.closeDropdown();
    }
  }

  private getResolvedName(): string {
    // Find the nearest parent form with data-form-name attribute
    let parent = this.hostElement.parentElement;
    while (parent) {
      if (parent.tagName.toLowerCase() === 'form' && parent.hasAttribute('data-form-name')) {
        const formName = parent.getAttribute('data-form-name');
        if (formName && this.name) {
          return `${formName}-${this.name}`;
        }
      }
      parent = parent.parentElement;
    }
    return this.name;
  }

  private handleFocus = () => {
    this.clearFieldError.emit(this.getResolvedName());
  };

  componentDidLoad() {
    if (!Build.isBrowser) return;
    const dropdown = this.hostElement.querySelector('.select__dropdown');
    if (dropdown) {
      dropdown.addEventListener(
        'wheel',
        (e: WheelEvent) => {
          const target = e.currentTarget as HTMLElement;
          const { scrollTop, scrollHeight, clientHeight } = target;
          const delta = e.deltaY;
          if ((delta < 0 && scrollTop === 0) || (delta > 0 && scrollTop + clientHeight >= scrollHeight)) {
            e.preventDefault();
          }
        },
        { passive: false },
      );

      // New handler for keyboard events
      dropdown.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
          const { scrollTop, scrollHeight, clientHeight } = dropdown;
          if ((e.key === 'ArrowUp' && scrollTop > 0) || (e.key === 'ArrowDown' && scrollTop + clientHeight < scrollHeight)) {
            e.preventDefault();
          }
        }
      });
    }
    window.addEventListener('scroll', this.handleDropdownReposition, true);
    window.addEventListener('resize', this.handleDropdownReposition, true);
  }

  private handleDropdownReposition = () => {
    if (this.isOpen) {
      this.updateDropdownCoords();
    }
  };

  private updateDropdownCoords = () => {
    if (this.triggerRef && this.hostElement) {
      const triggerRect = this.triggerRef.getBoundingClientRect();
      const parentRect = this.hostElement.getBoundingClientRect();
      this.dropdownCoords = {
        left: triggerRect.left - parentRect.left,
        width: triggerRect.width,
        top: 0,
      };
    }
  };

  render() {
    // Generate id only if both label and name are present
    const hasLabel = !!this.label;
    let selectId: string | undefined = undefined;
    const resolvedName = this.getResolvedName();
    if (hasLabel && this.name) {
      if (Build.isBrowser && !document.getElementById(this.name)) {
        selectId = this.name;
      } else {
        selectId = resolvedName;
      }
    }
    const labelId = hasLabel ? `${this.getResolvedName()}-label` : undefined;
    const selectedOption = this.optionList.find(opt => opt.value === this.value);
    const displayText = selectedOption ? selectedOption.label : this.placeholder;
    const hasError = this.error === 'true';
    let errorText = this.errorMessage;
    if (!errorText && hasError) {
      if (this.errorType === 'required') errorText = this.requiredMessage || 'This field is required';
      else if (this.errorType === 'invalid') errorText = this.invalidMessage || 'Invalid value';
      else errorText = 'This field is invalid';
    }
    return (
      <div
        class={{
          'select': true,
          'select--disabled': this.disabled,
          'select--open': this.isOpen,
          'select--top': this.dropdownPosition === 'top',
          'select--error': hasError,
        }}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={this.isOpen ? 'true' : 'false'}
        aria-controls="select-dropdown"
        aria-label={this.placeholder}
        tabIndex={-1}
      >
        {hasLabel && (
          <div
            id={labelId}
            class="select__label"
            role="button"
            tabIndex={-1}
            aria-label={this.label}
            onClick={e => {
              e.preventDefault();
              this.openDropdown();
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                this.openDropdown();
              }
            }}
          >
            {this.label}
          </div>
        )}
        <button
          ref={el => (this.triggerRef = el)}
          type="button"
          class={{
            'select__trigger': true,
            'select__trigger--error': hasError,
          }}
          onClick={this.toggleDropdown}
          onFocus={() => {
            if (!this.isOpen) {
              this.focusedOptionIndex = -1;
            }
          }}
          disabled={this.disabled}
          {...(labelId ? { 'aria-labelledby': labelId } : {})}
          tabIndex={this.disabled ? -1 : 0}
        >
          <span
            class={{
              'select__value': true,
              'select__value--placeholder': !this.value,
            }}
          >
            {displayText}
          </span>
          <span class="select__arrow" aria-hidden="true">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16.293 8.29297L12 12.586L7.70697 8.29297L6.29297 9.70697L12 15.414L17.707 9.70697L16.293 8.29297Z" fill="black" />
            </svg>
          </span>
        </button>
        {this.isOpen && this.dropdownCoords && (
          <div
            id="select-dropdown"
            class="select__dropdown"
            role="listbox"
            aria-activedescendant={this.focusedOptionIndex >= 0 ? `option-${this.focusedOptionIndex}` : undefined}
            tabIndex={0}
            style={{
              position: 'absolute',
              left: `${this.dropdownCoords.left}px`,
              width: `${this.dropdownCoords.width}px`,
              zIndex: '2000',
            }}
          >
            {this.optionList.map((option, index) => (
              <button
                ref={el => (this.optionRefs[index] = el)}
                id={`option-${index}`}
                key={option.value}
                class={{
                  'select__option': true,
                  'select__option--selected': option.value === this.value,
                  'select__option--focused': index === this.focusedOptionIndex,
                }}
                role="option"
                aria-selected={option.value === this.value ? 'true' : 'false'}
                type="button"
                onClick={() => this.selectOption(option)}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    this.selectOption(option);
                  }
                }}
                onMouseEnter={() => {
                  this.focusedOptionIndex = index;
                }}
                tabIndex={0}
              >
                <span class="select__option-label">{option.label}</span>
                <span class="select__option-check">
                  {option.value === this.value && (
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M9.99997 15.586L6.70697 12.293L5.29297 13.707L9.99997 18.414L19.707 8.70697L18.293 7.29297L9.99997 15.586Z" fill="black" />
                    </svg>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}
        <select
          class={{
            'select__native': true,
            'select__native--error': hasError,
          }}
          {...(selectId ? { id: selectId } : {})}
          name={this.getResolvedName()}
          disabled={this.disabled}
          required={this.required}
          onChange={this.handleSelectChange}
          onFocus={this.handleFocus}
          tabIndex={-1}
          aria-invalid={hasError ? 'true' : undefined}
        >
          {this.placeholder && (
            <option value="" disabled selected={!this.value}>
              {this.placeholder}
            </option>
          )}
          {this.optionList.map(option => (
            <option key={option.value} value={option.value} selected={option.value === this.value}>
              {option.label}
            </option>
          ))}
        </select>
        {hasError && errorText && (
          <me-text variant="small" class="form-error-message">
            {errorText}
          </me-text>
        )}
      </div>
    );
  }
}
